import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  TextField,
  Typography,
  useConfirm,
  useSnackbar,
} from '@munifocus/ui';
import type { CalendarFeedCreatedDto } from '@munifocus/contracts-gov';
import {
  useCalendarFeed,
  useCreateCalendarFeed,
  useRevokeCalendarFeed,
} from '../../lib/teamwork-api';
import { intlLocale } from '../../lib/relative-time';
import { SettingsSection as Section } from './SettingsSection';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * The private calendar subscription: statutory deadlines, scheduled
 * inspections and handover periods on the officer's own files, for Outlook,
 * Google Calendar or Apple Calendar.
 *
 * The link is shown once, when it is made — only its fingerprint is kept — so
 * regenerating is the way to see a link again, and it retires the old one.
 */
export function CalendarFeedSection() {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const confirm = useConfirm();
  const status = useCalendarFeed();
  const create = useCreateCalendarFeed();
  const revoke = useRevokeCalendarFeed();
  const [created, setCreated] = useState<CalendarFeedCreatedDto | null>(null);
  const locale = intlLocale(i18n.language);
  const active = status.data?.active ?? false;

  const format = (iso: string | null | undefined) =>
    iso
      ? frenchFirstOfMonth(
          new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date(iso),
          ),
        )
      : t('gov.teamwork.calendar.never');

  const generate = async () => {
    if (active) {
      const ok = await confirm({
        title: t('gov.teamwork.calendar.regenerateTitle'),
        body: t('gov.teamwork.calendar.regenerateBody'),
        confirmLabel: t('gov.teamwork.calendar.regenerate'),
        cancelLabel: t('common.actions.cancel'),
      });
      if (!ok) return;
    }
    create.mutate(undefined, {
      onSuccess: (data) => setCreated(data),
      onError: () => snackbar.error(t('gov.teamwork.calendar.error')),
    });
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      snackbar.success(t('gov.teamwork.calendar.copied'));
    } catch {
      snackbar.error(t('gov.teamwork.calendar.copyFailed'));
    }
  };

  return (
    <Section
      icon={Icons.Calendar}
      title={t('gov.teamwork.calendar.title')}
      description={t('gov.teamwork.calendar.description')}
    >
      <Stack spacing={2}>
        {status.isError && <Alert severity="error">{t('gov.teamwork.calendar.loadError')}</Alert>}
        {status.data && (
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>
            {active
              ? t('gov.teamwork.calendar.active', {
                  created: format(status.data.createdAt),
                  used: format(status.data.lastUsedAt),
                })
              : t('gov.teamwork.calendar.inactive')}
          </Typography>
        )}

        {created && (
          <Stack spacing={1}>
            <Alert severity="warning">{t('gov.teamwork.calendar.shownOnce')}</Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                value={created.url}
                label={t('gov.teamwork.calendar.link')}
                slotProps={{ htmlInput: { readOnly: true, 'data-testid': 'calendar-url' } }}
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="outlined"
                startIcon={<Icons.Copy size={iconSize.sm} />}
                onClick={() => void copy(created.url)}
              >
                {t('gov.teamwork.calendar.copy')}
              </Button>
            </Stack>
            <Link href={created.webcalUrl} variant="body2">
              {t('gov.teamwork.calendar.subscribe')}
            </Link>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: palette.text.secondary }}>
              <li>
                <Typography variant="body2">{t('gov.teamwork.calendar.howOutlook')}</Typography>
              </li>
              <li>
                <Typography variant="body2">{t('gov.teamwork.calendar.howGoogle')}</Typography>
              </li>
              <li>
                <Typography variant="body2">{t('gov.teamwork.calendar.howApple')}</Typography>
              </li>
            </Box>
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant={active ? 'outlined' : 'contained'}
            disabled={create.isPending || status.isLoading}
            onClick={() => void generate()}
          >
            {active ? t('gov.teamwork.calendar.regenerate') : t('gov.teamwork.calendar.create')}
          </Button>
          {active && (
            <Button
              color="error"
              variant="outlined"
              disabled={revoke.isPending}
              onClick={async () => {
                const ok = await confirm({
                  title: t('gov.teamwork.calendar.revokeTitle'),
                  body: t('gov.teamwork.calendar.revokeBody'),
                  confirmLabel: t('gov.teamwork.calendar.revoke'),
                  cancelLabel: t('common.actions.cancel'),
                  tone: 'danger',
                });
                if (!ok) return;
                revoke.mutate(undefined, {
                  onSuccess: () => {
                    setCreated(null);
                    snackbar.success(t('gov.teamwork.calendar.revoked'));
                  },
                });
              }}
            >
              {t('gov.teamwork.calendar.revoke')}
            </Button>
          )}
        </Stack>
      </Stack>
    </Section>
  );
}
